import { useState, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { Search, ChevronRight } from "lucide-react"

import * as api from "@/api/cogeser"
import { useAuth } from "@/hooks/useAuth"
import type { IUser, ICustomer, IPaginatedResponse } from "@/types"

// ----------------------------------------------------------------------------

export default function Customers() {
  // Hooks
  const { token } = useAuth()
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ["app-users", page, search],
    queryFn: () =>
      api.getUsers({ page, limit: 20, search }, token!) as Promise<IPaginatedResponse<IUser>>,
    enabled: !!token,
  })

  // Only show users with role "user" (app registrations)
  const users = (data?.data || []).filter((u) => u.role === "user")
  const totalCount = data?.count || 0

  // Fetch linked customers to get nome/cognome
  const userIds = useMemo(() => users.map((u) => u.id), [users])

  const { data: customersData } = useQuery({
    queryKey: ["customers-for-list", userIds],
    queryFn: () =>
      api.getCustomers({ limit: 100 }, token!) as Promise<{ data: ICustomer[] }>,
    enabled: !!token && userIds.length > 0,
  })

  // Map user ID → customer for quick lookup
  const customerByUserId = useMemo(() => {
    const map = new Map<string, ICustomer>()
    for (const c of customersData?.data || []) {
      const uid = typeof c.user === "string" ? c.user : (c.user as any)?.id
      if (uid) map.set(uid, c)
    }
    return map
  }, [customersData])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Utenti Iscritti</h1>
        <p className="mt-1 text-sm text-gray-500">Utenti registrati all'app Cogeser</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Cerca per email o nome..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Nome</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Cognome</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Registrato il</th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr><td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">Caricamento...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">Nessun utente trovato</td></tr>
            ) : (
              users.map((user) => {
                const customer = customerByUserId.get(user.id)
                return (
                  <tr
                    key={user.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/customers/${user.id}`)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
                          {(customer?.name || user.displayName || user.email).charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {customer?.name || user.displayName || "-"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {customer?.surname || "-"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{user.email}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString("it-IT") : "-"}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <ChevronRight className="ml-auto h-4 w-4 text-gray-400" />
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
        {totalCount > 20 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3">
            <p className="text-sm text-gray-500">{totalCount} risultati</p>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm disabled:opacity-50">Precedente</button>
              <button onClick={() => setPage((p) => p + 1)} disabled={page * 20 >= totalCount} className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm disabled:opacity-50">Successivo</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
